// ** React Imports
import { useState, useEffect, useMemo, createContext, useContext } from "react"

// ** Intl Provider Import
import { IntlProvider } from "react-intl"

// ** Store
import { dispatch } from "@redux/store"

// ** Locale bundles
import {
  enMessages,
  arMessages,
  getDefaultLocale,
  messagesForLocale
} from "@src/locales"
import { getAdminFormatters } from "@src/utility/adminIntlFormat"

const menuMessages = {
  ar: arMessages,
  en: enMessages
}

export const messages = menuMessages

const Context = createContext({
  locale: "en",
  messages: enMessages,
  switchLanguage: () => {},
  formatters: getAdminFormatters("en")
})

export function useIntl() {
  return useContext(Context)
}

export default function TransProvider({ children }) {
  const initial = getDefaultLocale()
  const [locale, setLocale] = useState(initial)
  const [messagesState, setMessagesState] = useState(() =>
    messagesForLocale(initial)
  )

  const formatters = useMemo(() => getAdminFormatters(locale), [locale])

  const switchLanguage = (lang) => {
    const next = lang === "ar" ? "ar" : "en"
    setLocale(next)
    setMessagesState(menuMessages[next])
    try {
      window.localStorage.setItem("lang", next)
    } catch (e) {
      /* ignore */
    }
  }

  useEffect(() => {
    const root = document.documentElement
    root.setAttribute("lang", locale === "ar" ? "ar" : "en")
    root.setAttribute("dir", locale === "ar" ? "rtl" : "ltr")
    dispatch.layout.onHandleRtl(locale === "ar")
  }, [locale])

  const value = {
    locale,
    switchLanguage,
    messages: messagesState,
    formatters
  }

  return (
    <Context.Provider value={value}>
      <IntlProvider
        key={locale}
        locale={locale}
        messages={messagesState}
        defaultLocale="en"
      >
        {children}
      </IntlProvider>
    </Context.Provider>
  )
}
